/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Dashboard
 * (C) Copyright IBM Corp. 2020, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Container, FlexLayout, Label, SVGIcon, VSpacer } from '@ba/ba-ui-carbon-toolkit';
import DropDownPalette from './DropDownPalette';
import CustomPalette from '../../dialog/CustomPalette';
import StringResources from 'nls/StringResources';
import PaletteHelper from '../../utilities/PaletteHelper';
import KeyCodes from '../../lib/@waca/core-client/js/core-client/ui/KeyCodes';

// this may need to change to a prop if QuickPaletteSelection gets picked up by reporting(authoring), no short-term plan for that
const COMPONENT_IDENTIFIER = 'dashboard';

class QuickPaletteSelection extends Component {
	static propTypes = {
		/** Icons Api */
		icons: PropTypes.object.isRequired, // The Icons Feature
		/** Current selected palette id */
		selectedId: PropTypes.string.isRequired,
		/** Default selected palette id if cannot find the palette selected id */
		defaultSelectedId: PropTypes.string.isRequired,
		/** paletteService */
		paletteService: PropTypes.object.isRequired, // was glass context
		/** Palette type name supported. One of  */
		paletteTypeName: PropTypes.oneOf(['ColorPalette', 'HeatPalette', 'ConditionalPalette']),
		/** Property name for the palette property, used by palette service for MRU  */
		propertyName: PropTypes.string.isRequired,
		/** callback function to get palettes */
		getPalettes: PropTypes.func.isRequired,
		/** callback function to call during selection change */
		onChange: PropTypes.func.isRequired,
		/** callback function to call when a new custom palette get created */
		onCreatePalette: PropTypes.func.isRequired,
		/** callback function to call to manage color palette */
		onManageColorPalette: PropTypes.func.isRequired,
		/** Extra options for the svg to pass to Flyout */
		dropDownOptions: PropTypes.shape({
			className: PropTypes.string,
			tabIndex: PropTypes.number
		})
	};

	static defaultProps = {
		dropDownOptions: {},
		paletteTypeName: 'ColorPalette'
	};

	constructor (props) {
		super(props);
		this.state= {
			isOpenCustomDialog: false
		};
	}

	render() {
		const {
			defaultSelectedId,
			dropDownOptions,
			getPalettes,
			icons,
			onManageColorPalette,
			paletteTypeName,
			paletteService,
			selectedId
		} = this.props;
		const { isOpenCustomDialog } = this.state;
		return (
			<React.Fragment>
				<FlexLayout
					className={'authoring-quick-palette-container'}
					direction={'row'}
					alignItems={'center'}
					justifyContent={'space-between'}
				>
					<Label
						className={'authoring-quick-palette-label'}
						label={StringResources.get('colorPaletteLabel')}
					/>
					<Container
						title={StringResources.get('createPalette')}
						aria-label={StringResources.get('createPalette')}
						tabIndex={0}
						onKeyDown={e => {
							if (e.keyCode === KeyCodes.ENTER || e.keyCode === KeyCodes.SPACE) {
								this.setState({ isOpenCustomDialog: true });
								// Prevent browser's default action: space does a PageDown
								e.preventDefault();
							}
						}}
					>
						<SVGIcon
							iconId={icons.getIcon('add16').id}
							size={'small'}
							onClick={() => {
								this.setState({ isOpenCustomDialog: true });
							}}
						/>
					</Container>
					{isOpenCustomDialog && this._renderCustomDialog() }
				</FlexLayout>
				<VSpacer size={1}/>
				<DropDownPalette
					{...dropDownOptions}
					getPalettes={getPalettes}
					paletteService={paletteService}
					selectedId={selectedId}
					defaultSelectedId={defaultSelectedId}
					onChange={this._onPaletteChanged.bind(this)}
					onManageColorPalette={onManageColorPalette}
					paletteTypeName={paletteTypeName}
				/>
			</React.Fragment>
		);
	}

	_renderCustomDialog() {
		const { paletteService } = this.props;
		return (
			<CustomPalette
				key='createPalette'
				paletteService={paletteService}
				isDialogOpen={true}
				paletteType={'standard'}
				onPaletteCreated={this._onPaletteCreated.bind(this)}
				removeDialog={() => {
					this.setState({ isOpenCustomDialog: false });
				}}
			/>
		);
	}

	_onPaletteCreated(paletteId) {
		const newCMPaletteId = PaletteHelper.getCustomPaletteId(paletteId);
		const { onCreatePalette, paletteService } = this.props;
		this.setState({ isOpenCustomDialog: false });
		return paletteService.getPalette(paletteId)
			.then(this._addPaletteToMru.bind(this))
			.then(() => onCreatePalette(newCMPaletteId));
	}

	_onPaletteChanged(palette) {
		return this._addPaletteToMru(palette).then(
			() => { this.props.onChange(palette); }
		);
	}

	// logic copied from ChangePaletteView._handleUpdatingMRU
	_addPaletteToMru(palette) {
		// Don't add local palettes to the MRU list
		const isLocal = palette.isMine === false && palette.public === false;
		if ( isLocal ) {
			return Promise.resolve();
		}
		// Dashboard has a different list of system palettes for heat and conditional.  Make sure
		// we keep 3 palettes in the MRU for both sets
		const trimSystemByPropName = this.props.propertyName === 'condColorPalette' || this.props.propertyName.indexOf('contColorPalette') === 0 ? true : false;
		return this.props.paletteService.addRecentlyUsedPalette({
			palette: palette,
			isSystem: !!palette.system,
			component: COMPONENT_IDENTIFIER,
			propName: this.props.propertyName,
			trimSystemByPropName: trimSystemByPropName
		});
	}
}

export default observer(QuickPaletteSelection);



// WEBPACK FOOTER //
// ./src/js/components/quickPaletteSelection/QuickPaletteSelection.js